import { DataNode } from 'rc-tree/lib/interface';

import { CreateIssueMetaResponse } from '../../../../src/api/issueApi';
import { OptionItemObject } from '../../../../src/shared_models/interfaces';
import { SaveConfigModel } from '../../../../src/storage/interfaces';
import { isFirstBreadcrumbLevel } from '../components/config/CustomFieldConfig';
import {
    IssueItem,
    IssueNormalized,
    IssueWithCustomFieldFilledResponse,
    OptionWithUpdatedName,
    ParentOptionData,
    ParentsData,
} from '../components/config/interfaces';
import { getMatchingLevelInfo, getMatchingLevelInfoByParent } from '../components/config/LevelsInformation';
import { OptionTranslationValue } from '../components/config/options-adder/interfaces';
import { OptionsAdderAllowedValueTypes } from '../components/config/options-adder/types';
import { OptionTranslated } from '../components/edit/FieldSelect';
import { ConfigModel, CustomDataNode, ErrorResponseObject, LangCodes, LevelsInfo } from '../interfaces';

import {
    CHILDREN,
    FIRST_NESTING_LEVEL,
    KEY,
    LEVEL_DESCRIPTION_KEY,
    LEVEL_DESCRIPTION_TRANSLATION_KEY,
    LEVEL_NAME_KEY,
    LEVEL_NAME_TRANSLATION_KEY,
    LEVELS_INFO_DEFAULTS,
    OPTIONS_REMOVED_SUCCESS_MESSAGE,
    PARENTS_UPDATE,
    SAVE_OPTIONS_SUCCESS_MESSAGE,
    UNDEFINED,
    UNDERSCORE,
    UPDATE_OPTION_NAME_SUCCESS_MESSAGE,
} from './constants';

export const getFieldTreeData = (loadedData: DataNode[] | string[]) => {
    const treeData = getTreeData(loadedData);
    const filteredTreeData = treeData.filter((dataItem: any) =>
        typeof dataItem.newNode === UNDEFINED ? true : !dataItem.newNode
    );
    removeRedundantNodesFromTree(filteredTreeData as OptionItemObject[]);
    filteredTreeData.length > 0 && updateDepthPropertyOnEveryTreeLevel(filteredTreeData as OptionItemObject[]);
    return filteredTreeData;
};

export const hasValidLength = (value: any) => value && value.length > 0;

export const hasValidObjectLength = (object: any) => object && Object.keys(object).length > 0;

export const findOptionByPropertyAndValue = (
    tree: OptionItemObject[],
    keyToFind: string,
    valToFind: string
): OptionItemObject => {
    let foundObject: OptionItemObject;

    JSON.stringify(tree, (_, nestedValue) => {
        if (nestedValue && nestedValue[keyToFind] === valToFind) {
            foundObject = nestedValue;
        }

        return nestedValue as OptionItemObject;
    });

    return foundObject!;
};

export const updateDepthPropertyOnEveryTreeLevel = (tree: OptionItemObject[], depth = 0) => {
    tree.forEach(treeItem => {
        treeItem.depth = depth;

        if (treeItem.children && treeItem.children.length > 0) {
            updateDepthPropertyOnEveryTreeLevel(treeItem.children, depth + 1);
        }
    });
};

export const removeRedundantNodesFromTree = (tree: OptionItemObject[]): void => {
    const removeRedundantNodes = (tree: OptionItemObject[]) => {
        for (const treeItem of tree) {
            if (treeItem.children && treeItem.children.length > 0) {
                treeItem.children = treeItem.children.filter((dataItem: any) =>
                    typeof dataItem.newNode === UNDEFINED ? true : !dataItem.newNode
                );
            }

            treeItem.children?.length > 0 && removeRedundantNodes(treeItem.children);
        }
    };

    removeRedundantNodes(tree);
};

export const updateTreeOptionAndUpdateTreeDepths = (
    tree: OptionItemObject[],
    currentKey: string,
    currentOptions: OptionItemObject[],
    setter: (options: OptionItemObject[]) => void
) => {
    const updatedTree = [...tree];

    const update = (tree: OptionItemObject[], currentKey: string, currentOptions: OptionItemObject[]) => {
        for (const treeItem of tree) {
            if (treeItem.key === currentKey) {
                treeItem.children = currentOptions;

                break;
            }

            treeItem.children?.length > 0 && update(treeItem.children, currentKey, currentOptions);
        }
    };

    update(updatedTree, currentKey, currentOptions);
    setter(updatedTree);
};

export const updatePropertyInOption = (
    tree: OptionItemObject[],
    optionProperty: string,
    optionKey: string,
    value: OptionsAdderAllowedValueTypes,
    setter: (options: OptionItemObject[]) => void
): void => {
    const updatedTree = [...tree];

    const updateProperty = (
        tree: OptionItemObject[],
        optionProperty: string,
        optionKey: string,
        value: OptionsAdderAllowedValueTypes
    ) => {
        for (const treeItem of tree) {
            if (treeItem.key === optionKey) {
                treeItem[optionProperty] = value;

                break;
            }

            treeItem.children?.length > 0 && updateProperty(treeItem.children, optionProperty, optionKey, value);
        }
    };

    updateProperty(updatedTree, optionProperty, optionKey, value);

    updateDepthPropertyOnEveryTreeLevel(updatedTree);

    setter(updatedTree);
};

export const updateArrayWithItemsOnGivenDepthLevel = (
    tree: OptionItemObject[],
    depthLevel: number,
    arrayToUpdate: OptionItemObject[],
    isParentsUpdate: boolean
) => {
    for (const treeItem of tree) {
        if (treeItem.depth === (isParentsUpdate ? depthLevel - 1 : depthLevel)) {
            arrayToUpdate.push(treeItem);
        }

        if (treeItem.children && treeItem.children.length > 0) {
            updateArrayWithItemsOnGivenDepthLevel(treeItem.children, depthLevel, arrayToUpdate, isParentsUpdate);
        }
    }
};

export const getAllOptionsByDepthLevel = (tree: OptionItemObject[], depthLevel: number): OptionItemObject[] => {
    const currentLevelOptions: OptionItemObject[] = [];

    updateArrayWithItemsOnGivenDepthLevel(tree, depthLevel, currentLevelOptions, !PARENTS_UPDATE);

    return currentLevelOptions;
};

const searchForChildRelatedParents = (
    tree: OptionItemObject[],
    currentOptionKey: string,
    wholeTree: OptionItemObject[],
    potentialParents: OptionItemObject[]
) => {
    for (const treeItem of tree) {
        if (treeItem.children?.length > 0) {
            const relatedChild = treeItem.children.find(option => option.key === currentOptionKey);

            if (relatedChild) {
                potentialParents.push(treeItem);
                const parentNode = getParentNode(wholeTree, treeItem.depth!, treeItem.key);

                if (parentNode) {
                    searchForChildRelatedParents([parentNode], treeItem.key, wholeTree, potentialParents);
                }
            } else {
                searchForChildRelatedParents(treeItem.children, currentOptionKey, wholeTree, potentialParents);
            }
        }
    }
};

export const getDepthLevelRelatedOptions = (tree: OptionItemObject[], depthLevel: number, currentOptionKey: string) => {
    const wholeTree = [...tree];
    const potentialLevelParents: OptionItemObject[] = [];
    const allParentLevelOptions: OptionItemObject[] = getAllOptionsByDepthLevel(tree, depthLevel - 1); // find parent of this level

    searchForChildRelatedParents(allParentLevelOptions, currentOptionKey, wholeTree, potentialLevelParents);

    const relatedParent = potentialLevelParents.find(item => item.depth! + 1 === depthLevel);

    // @ts-ignore
    return relatedParent ? relatedParent.children : [];
};

export const updateLevelOneOptions = (tree: OptionItemObject[], options: OptionItemObject[]): OptionItemObject[] => {
    const updatedTree = [...tree].filter(option => option.value);
    const existingOptionsKeys = updatedTree.map((existingOption: OptionItemObject) => existingOption.key);

    options.forEach((newOption: OptionItemObject) => {
        if (!existingOptionsKeys.includes(newOption.key)) {
            updatedTree.push(newOption);
        }
    });

    return updatedTree;
};

export const updateOrderInLevelOneOptions = (
    options: OptionItemObject[],
    setter: (options: OptionItemObject[]) => void
): void => {
    const updatedTree: OptionItemObject[] = [];

    options.forEach((option: OptionItemObject) => {
        updatedTree.push(option);
    });

    setter(updatedTree);
};

export const updateTreeWithCurrentState = (
    tree: OptionItemObject[],
    optionKey: string,
    currentNestingLevel: number,
    selectOptions: OptionItemObject[],
    setter: (options: OptionItemObject[]) => void,
    triggerAction: (savingTriggered: boolean) => void,
    isStorageSave: boolean,
    isStraightUpdate = false
) => {
    if (isFirstBreadcrumbLevel(currentNestingLevel)) {
        isStraightUpdate ? setter(selectOptions) : setter(updateLevelOneOptions(tree, selectOptions));
    } else {
        const parentNode = getParentNode(
            tree,
            currentNestingLevel - 1,
            selectOptions.length === 0 ? optionKey : selectOptions[0].key
        );
        updatePropertyInOption(tree, CHILDREN, parentNode ? parentNode.key : optionKey, selectOptions, setter);
    }

    setTimeout(() => {
        isStorageSave && triggerAction(true);
    }, 0); // make sure tree is updated before saving
};

export const getParentNode = (nodes: OptionItemObject[], childDepth: number, childKey: string): OptionItemObject | null => {
    const potentialParentNodes: OptionItemObject[] = [];
    let parentNode: OptionItemObject;

    updateArrayWithItemsOnGivenDepthLevel(nodes, childDepth, potentialParentNodes, PARENTS_UPDATE);

    for (const parent of potentialParentNodes) {
        const childNodes = parent.children;
        const childNode = typeof childNodes === UNDEFINED ? undefined : childNodes.find(node => node.key === childKey);

        if (childNode) {
            parentNode = parent;

            break;
        }
    }

    // @ts-ignore
    return parentNode ? parentNode : undefined;
};

export const removeOptionFromTree = (
    tree: OptionItemObject[],
    optionKey: string,
    currentNestingLevel: number,
    setter: (options: OptionItemObject[]) => void
) => {
    const updatedTree = [...tree];

    if (isFirstBreadcrumbLevel(currentNestingLevel)) {
        const filteredTree = updatedTree.filter(option => option.key !== optionKey);
        setter(filteredTree);
    } else {
        // TODO: optimize with for of and break
        const optionToRemove = findOptionByPropertyAndValue(updatedTree, KEY, optionKey);
        const parentNode = getParentNode(updatedTree, optionToRemove.depth!, optionKey);
        const filteredChildren = parentNode?.children.filter(child => child.key !== optionKey);
        updatePropertyInOption(updatedTree, CHILDREN, parentNode!.key, filteredChildren!, setter);
        updateDepthPropertyOnEveryTreeLevel(updatedTree);
    }
};

export const logResponseError = (error: ErrorResponseObject) => {
    if (error.responseText && error.responseText.errorMessages) {
        console.error('Some errors occurred: ', error.responseText.errorMessages.join(', '));

        return;
    }
    console.error(error);
};

export const getParentsDataForCurrentNestingLevel = (
    tree: OptionItemObject[],
    currentParentOptionKey: string,
    currentNestingLevel: number
): ParentsData | undefined => {
    if (currentNestingLevel === FIRST_NESTING_LEVEL) {
        return undefined;
    }

    updateDepthPropertyOnEveryTreeLevel(tree);

    let disabledParent: OptionItemObject | undefined;
    const potentialParents: OptionItemObject[] = [];
    const parentNode = findOptionByPropertyAndValue(tree, KEY, currentParentOptionKey);

    if (parentNode) {
        const allParentLevelOptions: OptionItemObject[] = getAllOptionsByDepthLevel(tree, 0);

        searchForChildRelatedParents(allParentLevelOptions, currentParentOptionKey, tree, potentialParents);
        potentialParents.sort((elementOne, elementTwo) => elementOne.depth! - elementTwo.depth!);
        disabledParent = potentialParents.find(parent => !parent.isEnabled);

        disabledParent =
            disabledParent ?
                disabledParent && disabledParent.depth! + 1 < currentNestingLevel ?
                    disabledParent
                :   undefined
            : !parentNode.isEnabled && parentNode.depth! + 1 < currentNestingLevel ? parentNode
            : undefined;

        potentialParents.push(parentNode);
    }

    return {
        parents: potentialParents
            .map(parent => ({
                value: parent.value,
                depth: parent.depth!,
            }))
            .filter(parent => parent.depth < currentNestingLevel - 1),
        disabledParent,
    };
};

export const handleBulkOptions = (
    newOptions: string[],
    currentOptions: OptionItemObject[],
    nestingLevel: number,
    setter: (updatedOptions: OptionItemObject[]) => void
) => {
    const updatedOptions = [...currentOptions];
    const pushNewOption = (option: string) => {
        const key = self.crypto.randomUUID();
        updatedOptions.push({
            value: option,
            isDefaultOption: false,
            isEnabled: true,
            key,
            children: [],
            depth: nestingLevel - 1,
            translation: {},
        });
        return key;
    };

    const firstOptionKey = pushNewOption(newOptions[0]);

    newOptions.slice(1).forEach((option: string) => {
        pushNewOption(option);
    });

    setter(updatedOptions);
    return firstOptionKey;
};

export const getCustomFieldId = (customField: string) => customField.substring(customField.indexOf(UNDERSCORE) + 1);

export const fetchIssuesWithMatchingOptionValue = (
    startAt = 0,
    fetchedData: IssueNormalized[] = [],
    fetchMethod: (
        startAt: number,
        customFieldId: string,
        fieldName: string,
        levelToCheck: number,
        optionValue: string | OptionWithUpdatedName[],
        levelParents: ParentOptionData[],
        isMultiOptionSearch: boolean
    ) => Promise<Response>,
    normalizeMethod: (issues: IssueItem[], customField: string) => IssueNormalized[],
    customField: string,
    fieldName: string,
    levelToCheck: number,
    optionValue: string | OptionWithUpdatedName[],
    levelParents: ParentOptionData[],
    isMultiOptionSearch = false
) =>
    fetchMethod(startAt, getCustomFieldId(customField), fieldName, levelToCheck, optionValue, levelParents, isMultiOptionSearch)
        .then(async (data: Response) => {
            const extractedData: IssueWithCustomFieldFilledResponse = await data.json();
            const { maxResults, issues, total } = extractedData;
            const dataNormalizedWithNonEmptyValues = normalizeMethod(issues, customField);

            fetchedData.push(...dataNormalizedWithNonEmptyValues);

            if (fetchedData.length === total || !('total' in extractedData)) {
                return fetchedData.filter(
                    (issue: IssueNormalized) => issue.customFieldValue.ids.length > 0 || issue.customFieldValue.values
                );
            }

            await fetchIssuesWithMatchingOptionValue(
                startAt + maxResults,
                fetchedData,
                fetchMethod,
                normalizeMethod,
                customField,
                fieldName,
                levelToCheck,
                optionValue,
                levelParents
            );
        })
        .catch((error: ErrorResponseObject) => logResponseError(error));

export const deepSearch = (data: OptionItemObject[], keyValue: string, tempObj: { found: OptionItemObject | undefined }) => {
    if (keyValue && data) {
        data.find(node => {
            if (node.key == keyValue) {
                tempObj.found = node;
                return node;
            }
            return deepSearch(node.children, keyValue, tempObj);
        });
        if (tempObj.found) {
            return tempObj.found;
        }
    }
    return undefined;
};
export const getOptionsWithUpdatedName = (
    initialOptionsState: OptionItemObject[],
    options: OptionItemObject[],
    currentLevelOptionsState: OptionItemObject[],
    parentKey: string
): OptionWithUpdatedName[] => {
    const modifiedOptions: OptionWithUpdatedName[] = [];
    const initialOptionsKeys = initialOptionsState.map(option => option.key);
    const currentStateOptionsToCompare = currentLevelOptionsState.filter(
        option => initialOptionsKeys.includes(option.key) && option.value
    );
    currentStateOptionsToCompare.forEach((option: OptionItemObject) => {
        const matchedInitialOption = initialOptionsState.find(initialOption => initialOption.key === option.key);
        const parent = deepSearch(options, parentKey, { found: undefined });

        if (matchedInitialOption && matchedInitialOption.value !== option.value) {
            modifiedOptions.push({
                previous: matchedInitialOption,
                current: option,
                parent,
            });
        }
    });

    return modifiedOptions;
};
export const getOptionsWithUpdatedNameForImport = (
    initialOptionsState: OptionItemObject[],
    options: OptionItemObject[],
    optionWithUpdatedName: OptionWithUpdatedName[],
    parent?: OptionItemObject
) => {
    options.reduce((acc, item) => {
        const existingOption = initialOptionsState.find(existingOption => existingOption.key === item.key);
        if (existingOption && existingOption.value !== item.value) {
            acc.push({
                previous: existingOption,
                current: item,
                parent: parent,
            });
        }
        if (existingOption?.children?.length && item.children?.length) {
            getOptionsWithUpdatedNameForImport(existingOption.children, item.children, optionWithUpdatedName, existingOption);
        }
        return acc;
    }, optionWithUpdatedName);

    return optionWithUpdatedName;
};

export const getSaveModelSuccessMessage = (isOptionDeletionInProgress: boolean, isOptionNameUpdated: boolean): string => {
    if (isOptionDeletionInProgress) {
        return OPTIONS_REMOVED_SUCCESS_MESSAGE;
    }

    if (isOptionNameUpdated) {
        return UPDATE_OPTION_NAME_SUCCESS_MESSAGE;
    }

    return SAVE_OPTIONS_SUCCESS_MESSAGE;
};

export const updateDefaultOptionInCurrentLevel = (
    currentOptions: OptionItemObject[],
    optionIndex: number,
    isDefault: boolean
): OptionItemObject[] => {
    let updatedCurrentOptions = [...currentOptions];

    updatedCurrentOptions = updatedCurrentOptions.map((option: OptionItemObject, index: number) => {
        const updatedOption = { ...option };
        updatedOption.isDefaultOption = optionIndex === index && isDefault ? isDefault : false;

        return updatedOption;
    });

    return updatedCurrentOptions;
};

export const getOptionLabel = (
    option: OptionTranslated | CustomDataNode,
    locale: LangCodes | undefined,
    isInSelectContext = true
): string => {
    if (locale && option.translation && option.translation[locale]) {
        return option.translation[locale] as string;
    } else if (isInSelectContext && 'label' in option) {
        return option.label;
    } else {
        return option.value || '';
    }
};

export const updateLevelInfoFirstOptionKey = (
    firstOptionKey: string,
    levelsInfo: LevelsInfo,
    currentLevel: number,
    currentParentOptionKey: string,
    setter: (levelsInfo: LevelsInfo) => void
): void => {
    const matchingLevelInfo = getMatchingLevelInfoByParent(levelsInfo, currentLevel, currentParentOptionKey);

    if (matchingLevelInfo) {
        const updatedLevelsInfo = { ...levelsInfo };
        const possibleInfos = updatedLevelsInfo[`_${currentLevel}`];
        const updatedInfos = possibleInfos.filter(infoItem => infoItem.parentOptionKey !== currentParentOptionKey);
        const updatedMatchingInfo = { ...matchingLevelInfo };

        updatedMatchingInfo.firstOptionKey = firstOptionKey;
        updatedInfos.push(updatedMatchingInfo);
        updatedLevelsInfo[`_${currentLevel}`] = updatedInfos;
        setter(updatedLevelsInfo);
    }
};

export const updateLevelsInfoNameOrDescriptionTranslation = (
    keyToUpdate: typeof LEVEL_NAME_TRANSLATION_KEY | typeof LEVEL_DESCRIPTION_TRANSLATION_KEY,
    value: OptionTranslationValue,
    levelsInfo: LevelsInfo,
    currentLevel: number,
    firstOptionKey: string,
    setter: (levelsInfo: LevelsInfo) => void,
    currentParentOptionKey: string
): void => {
    const updatedLevelsInfo = { ...levelsInfo };

    const possibleInfos = updatedLevelsInfo[`_${currentLevel}`];
    const matchingLevelInfo =
        firstOptionKey ?
            getMatchingLevelInfo(levelsInfo, currentLevel, firstOptionKey)
        :   getMatchingLevelInfoByParent(levelsInfo, currentLevel, currentParentOptionKey);

    if (matchingLevelInfo) {
        const updatedInfos = possibleInfos.filter(
            infoItem => infoItem.firstOptionKey !== firstOptionKey || infoItem.parentOptionKey !== currentParentOptionKey
        );
        const updatedMatchingInfo = { ...matchingLevelInfo };

        updatedMatchingInfo.levelInfo[keyToUpdate][value.language] = value.translation;
        updatedInfos.push(updatedMatchingInfo);
        updatedLevelsInfo[`_${currentLevel}`] = updatedInfos;
    } else {
        const infoObject = {
            parentOptionKey: currentParentOptionKey,
            firstOptionKey,
            levelInfo: {
                levelName: '',
                levelDescription: '',
                levelNameTranslation:
                    keyToUpdate === LEVEL_NAME_TRANSLATION_KEY ?
                        {
                            [value.language]: value.translation,
                        }
                    :   {},
                levelDescriptionTranslation:
                    keyToUpdate === LEVEL_DESCRIPTION_TRANSLATION_KEY ?
                        {
                            [value.language]: value.translation,
                        }
                    :   {},
            },
        };
        possibleInfos.push(infoObject);
        updatedLevelsInfo[`_${currentLevel}`] = possibleInfos;
    }

    setter(updatedLevelsInfo);
};

export const updateLevelsInfoNameOrDescription = (
    keyToUpdate: typeof LEVEL_NAME_KEY | typeof LEVEL_DESCRIPTION_KEY,
    value: string,
    levelsInfo: LevelsInfo,
    currentLevel: number,
    firstOptionKey: string | undefined,
    setter: (levelsInfo: LevelsInfo) => void,
    currentParentOptionKey: string
): void => {
    const updatedLevelsInfo = { ...levelsInfo };

    const possibleInfos = updatedLevelsInfo[`_${currentLevel}`];
    const matchingLevelInfo =
        firstOptionKey ?
            getMatchingLevelInfo(levelsInfo, currentLevel, firstOptionKey)
        :   getMatchingLevelInfoByParent(levelsInfo, currentLevel, currentParentOptionKey);

    if (matchingLevelInfo) {
        const updatedInfos = possibleInfos.filter(
            infoItem => infoItem.firstOptionKey !== firstOptionKey || infoItem.parentOptionKey !== currentParentOptionKey
        );
        const updatedMatchingInfo = { ...matchingLevelInfo };

        updatedMatchingInfo.levelInfo[keyToUpdate] = value;
        updatedInfos.push(updatedMatchingInfo);
        updatedLevelsInfo[`_${currentLevel}`] = updatedInfos;
    } else {
        const infoObject = {
            parentOptionKey: currentParentOptionKey,
            firstOptionKey,
            levelInfo: {
                levelName: keyToUpdate === LEVEL_NAME_KEY ? value : '',
                levelDescription: keyToUpdate === LEVEL_DESCRIPTION_KEY ? value : '',
                levelNameTranslation: {},
                levelDescriptionTranslation: {},
            },
        };
        possibleInfos.push(infoObject);
        updatedLevelsInfo[`_${currentLevel}`] = possibleInfos;
    }

    setter(updatedLevelsInfo);
};

const getParsedConcatenatedData = (data: string[]): DataNode[] | SaveConfigModel => {
    let concatenatedData = '';

    data.forEach(dataItem => {
        concatenatedData += dataItem;
    });

    return JSON.parse(concatenatedData);
};

export const getTreeData = (data: ConfigModel): DataNode[] => {
    if (data.length === 0) {
        return data as DataNode[];
    }

    if (typeof data[0] === 'string') {
        const parsedData = getParsedConcatenatedData(data as string[]);

        return Array.isArray(parsedData) ? parsedData : parsedData.treeData;
    }

    return data as DataNode[];
};

export const getLevelsInfoData = (data: ConfigModel): LevelsInfo => {
    if (typeof data[0] === 'string') {
        const parsedData = getParsedConcatenatedData(data as string[]);

        return Array.isArray(parsedData) ? { ...LEVELS_INFO_DEFAULTS } : parsedData.levelsInfo;
    }

    return { ...LEVELS_INFO_DEFAULTS };
};

export const isIssueFieldRequired = (field: string, issueMetadata?: CreateIssueMetaResponse) => {
    if (issueMetadata === undefined) return false;
    const fields = issueMetadata?.fields;
    const fieldToCheck = fields[field];

    return !!fieldToCheck?.required;
};

export const updateLevelsInfoFirstOptionKey = (
    currentFirstOptionKey: string,
    currentLevel: number,
    levelsInfo: LevelsInfo,
    updatedOptions: OptionItemObject[],
    setter: (levelsInfo: LevelsInfo) => void
): void => {
    const updatedLevelsInfo = { ...levelsInfo };
    const possibleInfos = updatedLevelsInfo[`_${currentLevel}`];
    const matchingLevelInfo = getMatchingLevelInfo(levelsInfo, currentLevel, currentFirstOptionKey);
    const updatedFirstOptionKey = updatedOptions[0].key;

    if (matchingLevelInfo && currentFirstOptionKey !== updatedFirstOptionKey) {
        const updatedInfos = possibleInfos.filter(infoItem => infoItem.firstOptionKey !== currentFirstOptionKey);
        const updatedMatchingInfo = { ...matchingLevelInfo };

        updatedMatchingInfo.firstOptionKey = updatedFirstOptionKey;
        updatedInfos.push(updatedMatchingInfo);
        updatedLevelsInfo[`_${currentLevel}`] = updatedInfos;
        setter(updatedLevelsInfo);
    }
};
